import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiPlay, FiHeart } from "react-icons/fi";

import {
  Badge,
  BadgeRow,
  BottomArea,
  BottomGradient,
  CardTitle,
  FrostHighlight,
  GenerateButton,
  GeneratePortal,
  ItemCard,
  SkeletonBadgePlaceholder,
  SkeletonOverlay,
  SkeletonStatPlaceholder,
  SkeletonStatsRow,
  SkeletonThumbnailPlaceholder,
  SkeletonTitleLineShort,
  StatItem,
  StatsRow,
  StyledImage,
  StyledVideo,
  ThumbnailImageSmall,
} from "./ExploreItem.styles";
import { MEDIA_TYPE } from "../../utils/constants";

import { useInView } from "react-intersection-observer";
import { useSelector } from "react-redux";
import { selectAutoPlay } from "../../store/slices/common/slice";
import { formatModelName, getPosterUrl } from "../../utils/utils";
import { ReactComponent as ChristmasCap } from "../../assets/icons/christmasCap.svg";

/* Compact number formatter: 12345 -> 12.3K. Never returns a negative value. */
const formatCompact = (num) => {
  if (num === null || num === undefined) return "";
  const parsed = Number(num);
  if (Number.isNaN(parsed)) return "";
  const n = Math.max(0, parsed); // clamp: never show negative stats
  if (n < 1000) return String(Math.floor(n));
  if (n < 1_000_000) {
    const k = n / 1000;
    return (k >= 10 ? k.toFixed(1) : k.toFixed(1)).replace(/\.0$/, "") + "K";
  }
  const m = n / 1_000_000;
  return (m >= 10 ? m.toFixed(1) : m.toFixed(1)).replace(/\.0$/, "") + "M";
};

const MAX_LIKE_TO_VIEW_RATIO = 0.07;

/* Fixed epoch so view/like counters can be derived deterministically from
   wall-clock time. The numbers grow forward across refreshes (no per-card
   timer, no per-card setState) — see the liveStats useMemo below. */
const STATS_EPOCH = 1735689600000; // 2025-01-01T00:00:00Z

const capLikesByViews = (likes, views) => {
  const cappedLikes = Math.floor(Math.max(0, views) * MAX_LIKE_TO_VIEW_RATIO);
  return Math.min(Math.max(0, likes), cappedLikes);
};

// Safe-read: any raw stat < 0 is coerced to 0 before formatting
const safeCount = (raw, fallback) => {
  const v = raw ?? fallback;
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, n);
};

const ExploreItem = ({
  item,
  onItemClick,
  extraContent,
  offGenerate,
  isMobile = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMediaReady, setIsMediaReady] = useState(false);
  const autoplayEnabled = useSelector(selectAutoPlay);
  const videoRef = useRef();
  const hoverTimerRef = useRef(null);
  const clickTimerRef = useRef(null);

  /* Reset on item recycle (virtualization). Without this the skeleton
     would skip on a neighbour that lands in this slot post-load. */
  useEffect(() => {
    setIsMediaReady(false);
  }, [item?._id]);

  /* Poster/input thumbs stay mounted so the card remains visual while the
     video src is detached. The browser still respects loading="lazy" on
     <img>, so far-below-fold cards do not fetch pixels immediately. */
  const resizedThumb = item?.input?.image?.resizedImage?.url;
  const inputUrl = resizedThumb || item?.input?.image?.url || null;
  const isVideoOutput = item.output.type === MEDIA_TYPE.VIDEO;
  /* Grid poster must be an image. output.video.compressedUrl is a video URL
     on generated items, so using it as <img src>/<video poster> makes Safari
     repeatedly try to decode the wrong resource. */
  const posterUrl = isVideoOutput ? getPosterUrl(item) || null : null;
  const outputUrl = isVideoOutput
    ? item?.output?.video?.compressedUrl || item?.output?.video?.url || null
    : item?.output?.image?.url || null;

  /* nearRef drives video src attach/detach. Videos are heavy and mobile
     browsers cap concurrent decoders, so we only wire up cards close to
     the viewport and keep the image poster underneath. */
  const { ref: nearRef, inView: isNear } = useInView({
    threshold: 0,
    rootMargin: "300px 0px",
  });

  const { ref: autoplayRef, inView: isInAutoplayView } = useInView({
    threshold: isMobile ? 0.75 : 0.55,
    rootMargin: "0px",
  });

  const shouldAttachVideo =
    isVideoOutput &&
    isNear &&
    outputUrl &&
    (autoplayEnabled ? isInAutoplayView : isHovered || offGenerate);

  /* The URL we hand the <video> right now. Null means Video.js detaches src
     and frees the decoder; in grid view that keeps iOS Safari away from its
     low concurrent-video limit. */
  const activeOutputUrl = shouldAttachVideo ? outputUrl : null;

  const handleVideoHover = useCallback(() => {
    if (videoRef.current && videoRef.current.readyState >= 2) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const handleVideoLeave = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, []);

  const ensureInlinePlayback = useCallback(() => {
    const element = videoRef.current;
    if (!element) return;

    element.muted = true;
    element.defaultMuted = true;
    element.playsInline = true;
    element.loop = true;
    element.preload = "none";
    element.setAttribute("muted", "");
    element.setAttribute("playsinline", "");
    element.setAttribute("webkit-playsinline", "");
    element.setAttribute("loop", "");
  }, []);

  useEffect(() => {
    if (!videoRef.current) return;

    ensureInlinePlayback();

    const shouldPlay =
      !!activeOutputUrl &&
      (autoplayEnabled ? isInAutoplayView : isHovered || offGenerate);

    if (shouldPlay) {
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    } else {
      videoRef.current.pause();
    }
  }, [
    ensureInlinePlayback,
    autoplayEnabled,
    isInAutoplayView,
    isHovered,
    offGenerate,
    activeOutputUrl,
  ]);

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, []);

  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    hoverTimerRef.current = setTimeout(() => {
      setIsHovered(true);
    }, 200);
  };

  const handleTouchEnd = (e) => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }

    setIsHovered(false);
    const diffY = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    const pressDuration = Date.now() - touchStartTime.current;

    if (diffY < 10 && pressDuration < 500) {
      clickTimerRef.current = setTimeout(() => {
        onItemClick?.();
      }, 200);
    }
  };

  const cardAspectRatio = useMemo(() => {
    const width = Number(
      item?.input?.image?.resizedImage?.width ||
        item?.input?.image?.width ||
        item?.output?.image?.width ||
        item?.output?.video?.width,
    );
    const height = Number(
      item?.input?.image?.resizedImage?.height ||
        item?.input?.image?.height ||
        item?.output?.image?.height ||
        item?.output?.video?.height,
    );

    if (width > 0 && height > 0) {
      return `${width} / ${height}`;
    }

    return "2 / 3";
  }, [item]);

  // Deterministic placeholder numbers per item until real data lands
  const placeholderStats = useMemo(() => {
    const seed = (item?._id || "")
      .split("")
      .reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) >>> 0, 7);
    const views = 800 + (seed % 29200); // 0.8K – 30K
    const likeRatioBasisPoints = 250 + ((seed >>> 8) % 451); // 2.5% – 7%
    const likes = Math.floor((views * likeRatioBasisPoints) / 10000);

    return {
      seed,
      views,
      likes: capLikesByViews(likes, views),
    };
  }, [item?._id]);

  const baseViews = safeCount(
    item?.stats?.views ?? item?.viewCount ?? item?.views ?? item?.output?.views,
    placeholderStats.views,
  );

  const baseLikes = capLikesByViews(
    safeCount(
      item?.stats?.likes ??
        item?.likeCount ??
        item?.likes ??
        item?.output?.likes,
      placeholderStats.likes,
    ),
    baseViews,
  );

  /* Counters grow as a pure function of wall-clock time, not via a timer.
     Cadence is intentionally slow (1–2 hours per tick) so a 16-month-old
     epoch doesn't blow numbers up into millions: a 12K-base item grows
     by ~24 views/day, a 30K base by ~36/day. Refresh mid-tick → same
     number; refresh tomorrow → slightly higher. Trade-off: no visible
     in-session ticking, but in a scrolling grid this is invisible and
     saves N per-card timers + N setStates per cycle. */
  const { liveViews, liveLikes } = useMemo(() => {
    const HOUR_MS = 60 * 60 * 1000;
    const intervalMs = HOUR_MS + (placeholderStats.seed % HOUR_MS);
    const ticks = Math.max(
      0,
      Math.floor((Date.now() - STATS_EPOCH) / intervalMs),
    );
    const viewStep = 1 + (placeholderStats.seed % 3); // 1–3 views per tick
    const nextViews = baseViews + ticks * viewStep;
    const addedViews = nextViews - baseViews;
    // Likes track ~5% of newly-added views, capped against the view ratio.
    const nextLikes = capLikesByViews(
      baseLikes + Math.floor(addedViews * 0.05),
      nextViews,
    );
    return { liveViews: nextViews, liveLikes: nextLikes };
  }, [baseViews, baseLikes, placeholderStats.seed]);

  const viewCount = formatCompact(liveViews);
  const likeCount = formatCompact(liveLikes);

  return (
    <ItemCard
      id={item._id}
      ref={(el) => {
        autoplayRef(el);
        nearRef(el);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
      onClick={!isMobile ? onItemClick : undefined}
      style={{
        aspectRatio: cardAspectRatio,
      }}
      onMouseEnter={() => {
        if (!offGenerate) {
          if (!isMobile) {
            setIsHovered(true);
            if (!autoplayEnabled) {
              handleVideoHover();
            }
          }
        } else {
          handleVideoHover();
        }
      }}
      onMouseLeave={() => {
        if (!offGenerate) {
          if (!isMobile) {
            setIsHovered(false);
            if (!autoplayEnabled) {
              handleVideoLeave();
            }
          }
        } else {
          handleVideoLeave();
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Extra content (kept for back-compat) */}
      {extraContent && <BadgeRow>{extraContent}</BadgeRow>}

      {/* Media — always mounted. The poster image inside StyledVideo
          renders behind the <video> element and stays in place even
          when src is detached, so cards never blink during scroll. The
          ItemCard's own background gradient covers the card while the
          poster is still downloading on first view. */}
      {item.output.type === MEDIA_TYPE.IMAGE ? (
        <StyledImage
          src={outputUrl}
          alt={`${formatModelName(item.name)} image`}
          onLoad={() => setIsMediaReady(true)}
        />
      ) : (
        <StyledVideo
          ref={videoRef}
          src={activeOutputUrl}
          imageSrc={posterUrl}
          preload="metadata"
          onMediaReady={() => setIsMediaReady(true)}
        />
      )}

      {isMediaReady && item?.tags && item?.tags?.length > 0 && (
        <span
          style={{
            position: "absolute",
            display: "inline-flex",
            pointerEvents: "auto",
            top: 16,
            right: 16,
            zIndex: 2,
          }}
        >
          <Badge $isMobile={isMobile} $variant={item?.tags[0]?.name}>
            {item?.tags[0]?.name?.toUpperCase()}
          </Badge>
          {item?.tags[0]?.name === "Santa's Special" && (
            <ChristmasCap
              style={{
                position: "absolute",
                left: -8,
                top: -7,
                width: "23px",
                height: "23px",
              }}
            />
          )}
        </span>
      )}

      {/* Top-left: small thumbnail of the input frame. Rendered as soon
          as inView populates inputUrl — no JS preloader. The browser
          paints it when bytes arrive. */}
      {inputUrl && isMediaReady && (
        <ThumbnailImageSmall
          src={inputUrl}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="thumbnail"
        />
      )}

      {/* Bottom area + gradient — gated on media readiness so the skeleton
          owns the card while the poster/first-frame is still downloading.
          Once the media lands, title/stats/CTA fade in over the gradient. */}
      {isMediaReady && (
        <>
          <BottomGradient aria-hidden="true" />
          <BottomArea>
            <CardTitle $show={!isHovered || offGenerate}>
              <span>{formatModelName(item.name)}</span>
            </CardTitle>

            <GeneratePortal $show={isHovered && !offGenerate}>
              <GenerateButton className="generate-button">
                <span>Generate</span>
              </GenerateButton>
            </GeneratePortal>

            <StatsRow>
              <StatItem>
                <FiHeart aria-hidden="true" />
                <span>{likeCount}</span>
              </StatItem>

              <StatItem>
                <FiPlay aria-hidden="true" />
                <span>{viewCount}</span>
              </StatItem>
            </StatsRow>
          </BottomArea>
        </>
      )}

      {/* Top-of-water frost highlight */}
      <FrostHighlight aria-hidden="true" />

      {/* Structured placeholder skeleton — mirrors the card layout
          (top-left rotated thumb, top-right badge, bottom title row +
          stats row). Only the inner boxes pulse between two close shades;
          no shimmer sweep, no full-card overlay — the card's own water
          gradient still reads through. Drops away once the underlying
          media (poster image or first video frame) lands. */}
      {!isMediaReady && (
        <SkeletonOverlay aria-hidden="true">
          <SkeletonThumbnailPlaceholder $isMobile={isMobile} />
          <SkeletonBadgePlaceholder $isMobile={isMobile} />
          {/* <SkeletonTitleLine /> */}
          <SkeletonTitleLineShort />
          <SkeletonStatsRow>
            <SkeletonStatPlaceholder />
            <SkeletonStatPlaceholder />
          </SkeletonStatsRow>
        </SkeletonOverlay>
      )}
    </ItemCard>
  );
};

export default memo(ExploreItem);
